import { Box, Grid, Stack, Typography, useTheme } from "@mui/material";
import Button from "components/Button";
import AuthLeftPanel from "./AuthLeftSide";
import i18n from "localization/i18n";
import { useThemeColors } from "theme";
import useIsSmUp from "hooks/useIsSmUp";
import { useForm } from "react-hook-form";
import useTranslate from "hooks/useTranslate";
import { useSearchParams } from "react-router";
import { useEffect, useState } from "react";
import useAmnNavigate from "hooks/useAmnNavigate";
import { Snackbar } from "components/general/Snackbar";
import userApi from "adminPanel/user/userApi";
import reportHttpErrors from "helpers/reportHttpErrors";
import { setOrganizationId } from "adminPanel/user/userSlice";
import { useAppDispatch } from "app/hooks";
import ResetPasswordIcon from "components/icons/ResetPasswordIcon";
import PasswordTextField from "components/general/PasswordTextField";
import escapeRegExp from "helpers/escapeRegExp";
import patterns from "constants/patterns";
import getOrganization from "helpers/getOrganization";

export interface IResetPasswordFormData {
  email: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ResetPasswordPage() {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IResetPasswordFormData>();

  const { translate, messageTrans } = useTranslate("shared.resetPassword");
  const { amnNavigate } = useAmnNavigate();
  const dispatch = useAppDispatch();
  const colors = useThemeColors();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";
  const [disabled, setDisabled] = useState(false);
  const isSmUp = useIsSmUp();
  const style = {
    container: {
      height: "100vh",
    },
    cardHeader: {
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    },
    containerLogin: {
      display: "flex",
      justifyContent: "center",
      right: 0,
      alignItems: "center",
    },
    containerMobileLog: {
      backgroundImage: "url('/images/BGMobile.svg')",
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
      justifyContent: "center",
      alignItems: "center",
      paddingX: 2,
      height: "85dvh",
    },
    cardContent: {
      width: isSmUp ? "35vw" : "100dvw",
    },

    title: {
      fontSize: 28,
      fontWeight: 600,
      mb: 1,
    },
    Link: {
      textDecoration: "none",
      color: colors.brand.primary,
      fontWeight: 500,
      fontSize: 16,
      display: "inline-block",
      alignSelf: "center",
      cursor: "pointer",
    },
    langSelect: isSmUp
      ? {
          color: colors.brand.primary,
        }
      : {
          position: "absolute",
          top: 22,
          right: i18n.language == "en" ? 5 : "unset",
          left: i18n.language == "ar" ? 5 : "unset",
          color: "white",
        },
    warning: {
      fontSize: 14,
      color: colors.status.error,
    },
  };

  useEffect(() => {
    var code = searchParams.get("code");
    var email = searchParams.get("email");
    var organizationId = getOrganization();

    if (!code || !email || !organizationId) {
      amnNavigate("/login");
    }

    setValue("email", email!);
    setValue("code", code!);
    dispatch(setOrganizationId(organizationId));
  });

  const onSubmit = (data: IResetPasswordFormData) => {
    setDisabled(true);
    userApi
      .resetPassword(data.email, data.code, data.newPassword)
      .then(() => {
        amnNavigate("/login?passwordReset=1", undefined, { replace: true });
        Snackbar(translate("passwordResetSuccessfully"), "success");
      })
      .catch((e) => {
        reportHttpErrors(e.response, Snackbar);
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const cardHeader = (
    <Stack sx={style.cardHeader}>
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: "16px",
          bgcolor: isDarkTheme ? "#2F2B52" : "#EFEDFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <ResetPasswordIcon />
      </Box>
      <Typography sx={style.title}>{translate("resetPassword")}</Typography>
      <Stack sx={{
        alignItems: "center"
      }}>
        <Typography
          color="textSecondary"
          sx={{
            paddingLeft: 0.5,
            textAlign: isSmUp ? "center" : "",
          }}
          variant="bodyMediumRegular"
        >
          {translate("resetPasswordMsg")}
        </Typography>
      </Stack>
    </Stack>
  );

  const cardContent = (
    <form>
      <Stack sx={style.cardContent}>
        {cardHeader}
        <PasswordTextField
          margin="normal"
          id="newPassword"
          size="small"
          variant="outlined"
          type="password"
          name="newPassword"
          fullWidth
          label={translate("newPassword")}
          inputProps={register("newPassword", {
            required: {
              value: true,
              message: "requiredError",
            },
            pattern: {
              value: patterns.password,
              message: "invalidPassword",
            },
          })}
          data-testid="reset-password-new-field"
          error={Boolean(errors.newPassword)}
          helperText={messageTrans(errors.newPassword?.message as string)}
          showPasswordIcon={true}
        />
        <PasswordTextField
          margin="normal"
          id="confirmPassword"
          size="small"
          variant="outlined"
          type="password"
          name="confirmPassword"
          fullWidth
          label={translate("confirmNewPassword")}
          inputProps={register("confirmPassword", {
            required: {
              value: true,
              message: "requiredError",
            },
            pattern: {
              value: new RegExp(escapeRegExp(watch("newPassword") ?? "")),
              message: "confirmPasswordError",
            },
          })}
          data-testid="reset-password-confirm-field"
          error={Boolean(errors.confirmPassword)}
          helperText={messageTrans(errors.confirmPassword?.message as string)}
          showPasswordIcon={true}
        />
        <Stack sx={{
          gap: 3
        }}>
          <Button
            fullWidth
            onClick={handleSubmit(onSubmit)}
            type="submit"
            disableElevation
            variant="primary"
            disabled={disabled}
            data-testid="reset-password-submit"
            sx={{
              mt: 2,
            }}
          >
            {translate("resetPasswordBtn")}
          </Button>
        </Stack>
      </Stack>
    </form>
  );

  return isSmUp ? (
    <Grid container sx={style.container}>
      <Grid size={6}>
        <AuthLeftPanel />
      </Grid>
      <Grid size={6} sx={style.containerLogin}>
        {cardContent}
      </Grid>
    </Grid>
  ) : (
    <Grid container sx={style.containerMobileLog}>
      {cardContent}
    </Grid>
  );
}
